import { HttpClient } from '@angular/common/http';
import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import {
  arrowsSwapIcon,
  chartColumnStackedIcon,
  dollarIcon,
  gearIcon,
  gridIcon,
  menuIcon,
  sparklesIcon,
  SVGIcon,
} from '@progress/kendo-svg-icons';
import {TranslateService} from "@ngx-translate/core";
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { AuthService } from './auth.service';
import { ThemeSwitcherService } from './core/theme-switcher/theme-switcher.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.css',
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private themeSub?: Subscription;
  private currentTheme: 'light' | 'dark';
  private readonly _destroying$ = new Subject<void>();
  
  // Authentication properties
  isLoggedIn = false;
  userName = '';
  
  // Loading state for authentication
  isAuthLoading = true;
  authLoadingMessage = 'Please wait while we verify your credentials...';
  
  // Login failure state
  loginFailed = false;
  loginErrorMessage = 'You are not authorized to access this application.';
  loginErrorDetails: string | null = null;

  @HostBinding('class.dark') get hostDark(): boolean {
    return this.currentTheme === 'dark';
  }

  @HostBinding('class.theme-dark') get hostThemeDark(): boolean {
    return this.currentTheme === 'dark';
  }

  constructor(
    private translate: TranslateService,
    private themeSwitcher: ThemeSwitcherService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public auth: AuthService
  ) {
    this.translate.addLangs(['de', 'en-US', 'es-ES']);
    this.translate.setDefaultLang('en-US');
    this.translate.use('en-US');
    this.currentTheme = this.themeSwitcher.currentTheme;
  }

  ngOnInit() {
    this.themeSub = this.themeSwitcher.themeChanges.subscribe(
      (theme) => (this.currentTheme = theme),
    );

    // Dev bypass: skip MSAL entirely, use index.html globals
    if (this.auth.isDevBypass) {
      this.isLoggedIn = true;
      this.userName = this.auth.getUserDisplayName();
      this.isAuthLoading = false;
      this.removeInitialLoader();
      console.log('[DEV BYPASS] Auth bypassed. Using CURRENT_USER values from index.html.');
      return;
    }

    // Handle authentication state changes
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
        this.isAuthLoading = false;
        this.removeInitialLoader();
      });

    // Listen for login success
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);
        this.checkAndSetActiveAccount();
        this.loginFailed = false;
      });

    // Listen for login failure
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        this.isAuthLoading = false;
        this.loginFailed = true;
        this.loginErrorDetails = result.error?.message || null;
        this.removeInitialLoader();
      });

    this.checkAndSetActiveAccount();
  }

  checkAndSetActiveAccount() {
    const activeAccount = this.msalService.instance.getActiveAccount();
    
    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }

    this.isLoggedIn = this.auth.isLoggedIn();
    if (this.isLoggedIn) {
      this.userName = this.auth.getUserDisplayName();
    }
  }

  login() {
    this.loginFailed = false;
    this.loginErrorDetails = null;
    this.auth.loginRedirect();
  }

  logout() {
    this.auth.logoutRedirect();
  }

  retryLogin() {
    this.loginFailed = false;
    this.loginErrorDetails = null;
    this.isAuthLoading = true;
    this.auth.loginRedirect();
  }

  ngOnDestroy(): void {
    this.themeSub?.unsubscribe();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  getTheme() {
    return localStorage.getItem('user-theme');
  }

  /**
   * Remove the initial HTML loader from index.html
   */
  private removeInitialLoader(): void {
    const loader = document.getElementById('initial-loader');
    if (loader) {
      loader.style.opacity = '0';
      loader.style.transition = 'opacity 0.3s ease-out';
      setTimeout(() => loader.remove(), 300);
    }
  }

  title = 'RTA / EJDF';


}
